// Lịch hẹn dạng lịch Tháng / Tuần / Ngày cho màn Tư vấn cơ sở
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Popover, Tag } from 'antd';
import { ChevronLeft, ChevronRight, Headphones, UserRound } from 'lucide-react';
import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { agentOf, consultantOf, type Appt, type ApptStatus, type Consultant } from './data';
import { apptConsultant, apptStatus, isLate, type Ctx } from './logic';
import { APPT_STATUS, Avatar, hm } from './ui';

dayjs.extend(isoWeek);

type Mode = 'month' | 'week' | 'day';
const WEEK_DAYS = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'CN'];
const WEEK_DAYS_FULL = ['Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật'];
const START_H = 8;
const END_H = 21;
/** Lịch hẹn chưa có giờ kết thúc → mỗi lịch hiển thị như một khung 30 phút */
const SLOT_MIN = 30;

export const STATUS_HEX: Record<ApptStatus, string> = {
  booked: '#94a3b8', confirmed: '#3b82f6', arrived: '#0891b2', consulting: '#3e91ab', won: '#16a34a',
  deposit: '#65a30d', no_need: '#f97316', medical: '#db2777', noshow: '#ef4444', cancelled: '#cbd5e1',
};
const rgba = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

interface Ev { a: Appt; st: ApptStatus; color: string; min: number; col: number; cols: number }

/** Xếp lịch trùng giờ thành các cột song song; lịch không trùng ai thì chiếm cả bề ngang */
function layout(list: Appt[], ctx: Ctx): Ev[] {
  const out: Ev[] = [];
  let group: Ev[] = [];
  let ends: number[] = [];
  const flush = () => { group.forEach(e => { e.cols = ends.length; }); out.push(...group); group = []; ends = []; };
  for (const a of [...list].sort((x, y) => x.at - y.at)) {
    const d = dayjs(a.at);
    const min = d.hour() * 60 + d.minute();
    if (group.length && min >= Math.max(...ends)) flush();
    let col = ends.findIndex(e => e <= min);
    if (col < 0) { col = ends.length; ends.push(0); }
    ends[col] = min + SLOT_MIN;
    const st = apptStatus(a, ctx);
    group.push({ a, st, color: STATUS_HEX[st], min, col, cols: 1 });
  }
  flush();
  return out;
}

export default function ApptCalendar({ ctx, appts, consultants, actions }: { ctx: Ctx; appts: Appt[]; consultants: Consultant[]; actions: (a: Appt) => React.ReactNode }) {
  const [mode, setMode] = useState<Mode>('week');
  const [anchor, setAnchor] = useState<Dayjs>(() => dayjs(ctx.now));
  const today = dayjs(ctx.now);

  const byDate = useMemo(() => {
    const map: Record<string, Appt[]> = {};
    for (const a of appts) {
      if (a.bookedAt > ctx.now) continue;
      const k = dayjs(a.at).format('YYYY-MM-DD');
      if (!map[k]) map[k] = [];
      map[k].push(a);
    }
    Object.values(map).forEach(l => l.sort((x, y) => x.at - y.at));
    return map;
  }, [appts, ctx.now]);
  const on = (d: Dayjs) => byDate[d.format('YYYY-MM-DD')] ?? [];

  const days = mode === 'day' ? [anchor] : mode === 'week' ? Array.from({ length: 7 }, (_, i) => anchor.startOf('isoWeek').add(i, 'day')) : [];
  const count = mode === 'month'
    ? Object.entries(byDate).filter(([k]) => dayjs(k).isSame(anchor, 'month')).reduce((s, [, l]) => s + l.length, 0)
    : days.reduce((s, d) => s + on(d).length, 0);

  const step = (dir: number) => setAnchor(p => p.add(dir, mode === 'month' ? 'month' : mode === 'week' ? 'week' : 'day'));
  const openDay = (d: Dayjs) => { setAnchor(d); setMode('day'); };

  // Tuần / Ngày: tự cuộn tới giờ hiện tại (nếu đang xem hôm nay) hoặc lịch sớm nhất
  const scrollRef = useRef<HTMLDivElement>(null);
  const anchorKey = anchor.format('YYYY-MM-DD');
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || mode === 'month') return;
    const px = mode === 'week' ? 96 : 140;
    const shown = mode === 'week' ? days : [anchor];
    const first = shown.flatMap(d => on(d)).map(a => dayjs(a.at).hour() * 60 + dayjs(a.at).minute()).sort((x, y) => x - y)[0];
    const target = shown.some(d => d.isSame(today, 'day')) ? today.hour() * 60 + today.minute() - 60 : first ?? START_H * 60;
    el.scrollTop = Math.max(0, ((target - START_H * 60) / 60) * px - 20);
  }, [mode, anchorKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = mode === 'month'
    ? `tháng ${anchor.month() + 1} năm ${anchor.year()}`
    : mode === 'week'
      ? `Tuần ${anchor.startOf('isoWeek').format('D/M')} - ${anchor.startOf('isoWeek').add(6, 'day').format('D/M/YYYY')}`
      : `${WEEK_DAYS_FULL[anchor.isoWeekday() - 1]}, ${anchor.format('D/M/YYYY')}`;

  /** Thẻ chi tiết lịch hẹn + thao tác */
  const detail = (a: Appt) => {
    const l = ctx.m.leadById[a.leadId];
    const st = apptStatus(a, ctx);
    return (
      <div className="w-72 space-y-2 text-[12px]">
        <div className="flex items-start justify-between gap-2">
          <div className="leading-tight">
            <div className="text-[14px] font-bold text-gray-800 dark:text-gray-100">{l.name}</div>
            <div className="text-gray-500">{l.id} · {l.phone}</div>
          </div>
          <Tag color={APPT_STATUS[st].color} className="!m-0">{APPT_STATUS[st].label}</Tag>
        </div>
        <div className="rounded-md bg-gray-50 p-2 dark:bg-gray-700/50">
          <div className="font-semibold text-gray-700 dark:text-gray-200">{dayjs(a.at).format('HH:mm · DD/MM/YYYY')} · {a.kind} · {l.mang}</div>
          <div className="mt-0.5 text-gray-500">{a.note}</div>
          {isLate(a, ctx) && <div className="mt-0.5 font-semibold text-red-500">Quá giờ hẹn, khách chưa đến</div>}
        </div>
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300"><Headphones size={13} className="text-gray-400" />Telesales: <b>{agentOf(a.bookedBy)?.name}</b></div>
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300"><UserRound size={13} className="text-gray-400" />Tư vấn viên: <b>{consultantOf(apptConsultant(a, ctx))?.name ?? 'Chưa phân'}</b></div>
        <div className="border-t border-gray-100 pt-2 dark:border-gray-700">{actions(a) ?? <span className="text-gray-400">Không có thao tác cho lịch này</span>}</div>
      </div>
    );
  };
  const pop = (a: Appt, node: React.ReactElement) => (
    <Popover key={a.id} trigger="click" placement="right" content={detail(a)} destroyTooltipOnHide>{node}</Popover>
  );

  /* ---------- Tháng ---------- */
  const month = () => {
    const start = anchor.startOf('month').startOf('isoWeek');
    const weeks = Math.ceil(anchor.startOf('month').endOf('month').diff(start, 'day') / 7 + 0.01);
    const cells = Array.from({ length: weeks * 7 }, (_, i) => start.add(i, 'day'));
    return (
      <div className="min-w-[760px]">
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
          {WEEK_DAYS.map((d, i) => <div key={d} className={`py-2 text-center text-[11px] font-semibold uppercase ${i === 6 ? 'text-red-500' : 'text-gray-500'}`}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map(d => {
            const list = on(d);
            const inMonth = d.isSame(anchor, 'month');
            const isToday = d.isSame(today, 'day');
            return (
              <div key={d.valueOf()} className={`min-h-[118px] border-b border-r border-gray-100 p-1.5 dark:border-gray-700 ${inMonth ? '' : 'bg-gray-50/70 dark:bg-gray-800/40'}`}>
                <div className="mb-1 flex items-center justify-between">
                  {list.length ? <span className="text-[10px] text-gray-400">{list.length} lịch</span> : <span />}
                  <button type="button" onClick={() => openDay(d)}
                    className={`flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-[12px] font-semibold ${isToday ? 'bg-[#3e91ab] text-white' : inMonth ? 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700' : 'text-gray-400'}`}>
                    {d.date()}
                  </button>
                </div>
                <div className="space-y-0.5">
                  {list.slice(0, 3).map(a => {
                    const st = apptStatus(a, ctx);
                    return pop(a, (
                      <button type="button" className="flex w-full items-center gap-1 truncate rounded px-1 py-0.5 text-left text-[11px] hover:brightness-95"
                        style={{ background: rgba(STATUS_HEX[st], 0.14), color: '#1f2937' }}>
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: STATUS_HEX[st] }} />
                        <b className="shrink-0" style={{ color: STATUS_HEX[st] }}>{hm(a.at)}</b>
                        <span className={`truncate dark:text-gray-100 ${st === 'cancelled' ? 'line-through opacity-60' : ''}`}>{ctx.m.leadById[a.leadId].name}</span>
                      </button>
                    ));
                  })}
                  {list.length > 3 && <button type="button" onClick={() => openDay(d)} className="px-1 text-[11px] font-medium text-[#3e91ab] hover:underline">+{list.length - 3} lịch khác</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ---------- Tuần / Ngày: lưới giờ ---------- */
  const hours = Array.from({ length: END_H - START_H }, (_, i) => START_H + i);
  const nowMin = today.hour() * 60 + today.minute();
  const showNow = nowMin >= START_H * 60 && nowMin <= END_H * 60;
  const topOf = (min: number, px: number) => ((min - START_H * 60) / 60) * px;

  /** Thẻ lịch trên lưới giờ */
  const card = (ev: Ev, px: number, detailed: boolean) => {
    const l = ctx.m.leadById[ev.a.leadId];
    const w = 100 / ev.cols;
    const off = ev.st === 'cancelled' || ev.st === 'noshow';
    return pop(ev.a, (
      <button type="button"
        className="absolute z-10 overflow-hidden rounded-lg border bg-white text-left shadow-sm transition hover:z-30 hover:shadow-md dark:bg-gray-800"
        style={{ top: topOf(ev.min, px) + 2, height: (SLOT_MIN / 60) * px - 4, left: `calc(${ev.col * w}% + 3px)`, width: `calc(${w}% - 6px)`, borderColor: rgba(ev.color, 0.35) }}>
        <span className="absolute inset-y-0 left-0 w-1" style={{ background: ev.color }} />
        <span className="flex h-full flex-col justify-center gap-0.5 py-1 pl-2.5 pr-2" style={{ background: rgba(ev.color, 0.07) }}>
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="shrink-0 text-[11px] font-bold tabular-nums" style={{ color: ev.color }}>{hm(ev.a.at)}</span>
            <span className={`truncate text-[12px] font-semibold text-gray-800 dark:text-gray-100 ${off ? 'line-through opacity-60' : ''}`}>{l.name}</span>
            {detailed && <span className="ml-auto shrink-0 rounded-full px-1.5 text-[10px] font-semibold" style={{ background: rgba(ev.color, 0.15), color: ev.color }}>{APPT_STATUS[ev.st].label}</span>}
          </span>
          <span className="truncate text-[11px] text-gray-500 dark:text-gray-400">{detailed ? `${ev.a.kind} · ${l.mang} — ${ev.a.note}` : `${l.mang} · ${ev.a.kind}`}</span>
          {detailed && <span className="flex min-w-0 items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400"><Headphones size={11} className="shrink-0" /><span className="truncate">{agentOf(ev.a.bookedBy)?.name}</span></span>}
        </span>
      </button>
    ));
  };

  /** Cột giờ bên trái, kèm nhãn giờ hiện tại */
  const gutter = (px: number, withNow: boolean) => (
    <div className="relative w-14 shrink-0 text-[10px] text-gray-400">
      {hours.map(h => <div key={h} className="relative" style={{ height: px }}><span className="absolute -top-[7px] right-2 tabular-nums">{String(h).padStart(2, '0')}:00</span></div>)}
      {withNow && showNow && <span className="absolute right-1 z-20 -translate-y-1/2 rounded bg-red-500 px-1 py-px text-[10px] font-semibold text-white" style={{ top: topOf(nowMin, px) }}>{today.format('HH:mm')}</span>}
    </div>
  );

  /** Một cột lịch (một ngày hoặc một tư vấn viên) */
  const column = (key: string, list: Appt[], px: number, isToday: boolean, detailed: boolean) => (
    <div key={key} className={`relative min-w-0 flex-1 border-l border-gray-100 dark:border-gray-700 ${isToday ? 'bg-[#3e91ab]/[0.04]' : ''}`}>
      {hours.map(h => (
        <div key={h} className="border-t border-gray-100 dark:border-gray-700/70" style={{ height: px }}>
          <div className="border-b border-dashed border-gray-100 dark:border-gray-700/40" style={{ height: px / 2 }} />
        </div>
      ))}
      {isToday && showNow && <div className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-red-500" style={{ top: topOf(nowMin, px) }} />}
      {layout(list, ctx).map(ev => card(ev, px, detailed))}
    </div>
  );

  const headCls = 'sticky top-0 z-30 flex border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800';

  const week = () => {
    const px = 96;
    return (
      <div className="min-w-[860px]">
        <div className={headCls}>
          <div className="w-14 shrink-0" />
          {days.map(d => {
            const isToday = d.isSame(today, 'day');
            const n = on(d).length;
            return (
              <button type="button" key={d.valueOf()} onClick={() => openDay(d)} className="flex-1 border-l border-gray-100 py-2 text-center hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50">
                <div className={`text-[11px] font-semibold uppercase ${isToday ? 'text-[#3e91ab]' : d.isoWeekday() === 7 ? 'text-red-500' : 'text-gray-500'}`}>{WEEK_DAYS[d.isoWeekday() - 1]}</div>
                <div className={`mx-auto mt-1 flex h-8 w-8 items-center justify-center rounded-full text-[16px] font-bold ${isToday ? 'bg-[#3e91ab] text-white' : 'text-gray-800 dark:text-gray-100'}`}>{d.date()}</div>
                <div className="mt-0.5 h-4 text-[10px] text-gray-400">{n ? `${n} lịch` : ''}</div>
              </button>
            );
          })}
        </div>
        <div className="flex pt-2">
          {gutter(px, days.some(d => d.isSame(today, 'day')))}
          {days.map(d => column(String(d.valueOf()), on(d), px, d.isSame(today, 'day'), false))}
        </div>
      </div>
    );
  };

  /** Ngày: mỗi cột là một tư vấn viên của cơ sở, thêm cột lịch chưa phân người */
  const dayView = () => {
    const px = 140;
    const list = on(anchor);
    const isToday = anchor.isSame(today, 'day');
    const cols = [{ id: '_none', name: 'Chưa phân tư vấn viên' }, ...consultants];
    const of = (id: string) => list.filter(a => (apptConsultant(a, ctx) ?? '_none') === id);
    return (
      <div className="min-w-[640px]">
        <div className={headCls}>
          <div className="w-14 shrink-0" />
          {cols.map(c => {
            const cons = consultantOf(c.id);
            return (
              <div key={c.id} className="flex min-w-0 flex-1 items-center gap-2 border-l border-gray-100 px-3 py-2.5 dark:border-gray-700">
                {cons
                  ? <Avatar name={cons.name} color={cons.color} size={28} />
                  : <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700"><UserRound size={14} /></span>}
                <div className="min-w-0 leading-tight">
                  <div className="truncate text-[13px] font-semibold text-gray-800 dark:text-gray-100">{c.name}</div>
                  <div className="text-[11px] text-gray-400">{of(c.id).length} lịch</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="relative flex pt-2">
          {gutter(px, isToday)}
          {cols.map(c => column(c.id, of(c.id), px, isToday, true))}
          {!list.length && <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400">Không có lịch hẹn trong ngày này</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 p-3 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex">
            <button type="button" onClick={() => step(-1)} className="rounded-l-md border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"><ChevronLeft size={16} /></button>
            <button type="button" onClick={() => step(1)} className="rounded-r-md border border-l-0 border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"><ChevronRight size={16} /></button>
          </div>
          <button type="button" onClick={() => setAnchor(dayjs(ctx.now))} className="rounded-md border border-gray-200 px-3 py-1 text-[13px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700">Hôm nay</button>
          <h3 className="ml-1 text-[16px] font-bold text-gray-800 dark:text-gray-100">{title}</h3>
          <span className="text-xs text-gray-500">{count} lịch hẹn</span>
        </div>
        <div className="flex overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
          {([['month', 'Tháng'], ['week', 'Tuần'], ['day', 'Ngày']] as const).map(([k, label], i) => (
            <button type="button" key={k} onClick={() => setMode(k)}
              className={`px-4 py-1.5 text-[13px] font-medium transition ${i ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${mode === k ? 'bg-[#3e91ab] text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 border-b border-gray-100 px-3 py-1.5 text-[11px] text-gray-500 dark:border-gray-700">
        {(['booked', 'confirmed', 'arrived', 'consulting', 'won', 'deposit', 'no_need', 'medical', 'noshow', 'cancelled'] as ApptStatus[]).map(s => (
          <span key={s} className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_HEX[s] }} />{APPT_STATUS[s].label}</span>
        ))}
      </div>
      <div ref={scrollRef} className="max-h-[720px] overflow-auto">{mode === 'month' ? month() : mode === 'week' ? week() : dayView()}</div>
    </div>
  );
}
